import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CHAT_DEFAULT_MODEL, CHAT_MODEL_OPTIONS, CHAT_MODEL_PROVIDER_ORDER, CHAT_MODEL_STORAGE_KEY, CHAT_MODEL_VALUES, isChatModelSelectionEnabled } from '@/lib/ai/chat-models';
let unpersistedChatModel: string | undefined;

/** The owner-gated model to read at warm or send time, not from a stale hook instance. */
export function resolveChatModel(owner: string | null | undefined): string {
  return isChatModelSelectionEnabled(owner) ? unpersistedChatModel ?? readStoredChatModel() : CHAT_DEFAULT_MODEL;
}
export function readStoredChatModel(): string {
  if (typeof window === 'undefined') {
    return CHAT_DEFAULT_MODEL;
  }
  let rawValue: string | null = null;
  try {
    rawValue = window.localStorage.getItem(CHAT_MODEL_STORAGE_KEY);
  } catch {
    return CHAT_DEFAULT_MODEL;
  }
  if (!rawValue) {
    return CHAT_DEFAULT_MODEL;
  }
  try {
    const parsedValue = JSON.parse(rawValue) as unknown;
    return typeof parsedValue === 'string' && CHAT_MODEL_VALUES.has(parsedValue) ? parsedValue : CHAT_DEFAULT_MODEL;
  } catch {
    return CHAT_MODEL_VALUES.has(rawValue) ? rawValue : CHAT_DEFAULT_MODEL;
  }
}
function persistChatModel(model: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  unpersistedChatModel = model;
  try {
    window.localStorage.setItem(CHAT_MODEL_STORAGE_KEY, JSON.stringify(model));
    unpersistedChatModel = undefined;
  } catch {
    // Keep the in-memory selection even when storage writes fail.
  }
}
export function useChatModelSelection(owner) {
  const $ = _c(18);
  const [selectedModel, setSelectedModelState] = useState(CHAT_DEFAULT_MODEL);
  const [isModelSelectorOpenState, setIsModelSelectorOpenState] = useState(false);
  let t0;
  if ($[0] !== owner) {
    t0 = isChatModelSelectionEnabled(owner);
    $[0] = owner;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const canSelectChatModel = t0;
  const effectiveSelectedModel = canSelectChatModel ? selectedModel : CHAT_DEFAULT_MODEL;
  const isModelSelectorOpen = canSelectChatModel ? isModelSelectorOpenState : false;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = nextModel => {
      const normalizedModel = CHAT_MODEL_VALUES.has(nextModel) ? nextModel : CHAT_DEFAULT_MODEL;
      setSelectedModelState(normalizedModel);
      persistChatModel(normalizedModel);
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const setSelectedModel = t1;
  let t2;
  if ($[3] !== canSelectChatModel) {
    t2 = open => {
      setIsModelSelectorOpenState(canSelectChatModel ? open : false);
    };
    $[3] = canSelectChatModel;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const setIsModelSelectorOpen = t2;
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const storedModel = unpersistedChatModel ?? readStoredChatModel();
      setSelectedModelState(currentModel => currentModel === storedModel ? currentModel : storedModel);
      const handleStorageChange = event => {
        if (event.key && event.key !== CHAT_MODEL_STORAGE_KEY) {
          return;
        }
        unpersistedChatModel = undefined;
        const storedModel_0 = readStoredChatModel();
        setSelectedModelState(currentModel_0 => currentModel_0 === storedModel_0 ? currentModel_0 : storedModel_0);
      };
      window.addEventListener("storage", handleStorageChange);
      return () => {
        window.removeEventListener("storage", handleStorageChange);
      };
    };
    t4 = [];
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  bb0: {
    let t6;
    if ($[7] !== effectiveSelectedModel) {
      t6 = CHAT_MODEL_OPTIONS.find(model => model.value === effectiveSelectedModel);
      $[7] = effectiveSelectedModel;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    const found = t6;
    if (!found) {
      let t7;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = CHAT_MODEL_OPTIONS.find(_temp) ?? CHAT_MODEL_OPTIONS[0];
        $[9] = t7;
      } else {
        t7 = $[9];
      }
      t5 = t7;
      break bb0;
    }
    t5 = found;
  }
  const selectedModelInfo = t5;
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = CHAT_MODEL_PROVIDER_ORDER.map(_temp2);
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const modelsByProvider = t6;
  let t7;
  if ($[11] !== canSelectChatModel || $[12] !== effectiveSelectedModel || $[13] !== isModelSelectorOpen || $[14] !== selectedModel || $[15] !== selectedModelInfo || $[16] !== setIsModelSelectorOpen) {
    t7 = {
      canSelectChatModel,
      effectiveSelectedModel,
      isModelSelectorOpen,
      modelsByProvider,
      selectedModel,
      selectedModelInfo,
      setIsModelSelectorOpen,
      setSelectedModel
    };
    $[11] = canSelectChatModel;
    $[12] = effectiveSelectedModel;
    $[13] = isModelSelectorOpen;
    $[14] = selectedModel;
    $[15] = selectedModelInfo;
    $[16] = setIsModelSelectorOpen;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}
function _temp2(provider) {
  return {
    provider,
    models: CHAT_MODEL_OPTIONS.filter(model_1 => model_1.provider === provider)
  };
}
function _temp(model_0) {
  return model_0.value === CHAT_DEFAULT_MODEL;
}